import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import SettingMenu from './settingMenu';

interface CardData {
  cardNumber: string;
  name: string;
  expiryDate: string;
  cvv: string;
}

interface SavedCard {
  id: string;
  name: string;
  lastFour: string;
  type: 'mastercard' | 'visa';
  expiryDate: string;
  isPrimary: boolean;
}

const BillingPlansComponent: React.FC = () => {
  const [paymentMethod, setPaymentMethod] = useState<'card' | 'paypal'>('card');
  const [showCVV, setShowCVV] = useState(false);
  const [savedCards] = useState<SavedCard[]>([
    {
      id: '1',
      name: 'Tom McBride',
      lastFour: '9856',
      type: 'mastercard',
      expiryDate: '12/26',
      isPrimary: true
    },
    {
      id: '2',
      name: 'John Doe',
      lastFour: '4532',
      type: 'visa',
      expiryDate: '08/25',
      isPrimary: false
    }
  ]);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm<CardData>();

  const onSubmit = (data: CardData) => {
    console.log('Card data:', data);
  };

  const handleUpgradePlan = () => {
    console.log('Upgrade plan clicked');
  };

  const handleCancelSubscription = () => {
    console.log('Cancel subscription clicked');
  };

  const handleEditCard = (cardId: string) => {
    console.log('Edit card:', cardId);
  };

  const handleDeleteCard = (cardId: string) => {
    console.log('Delete card:', cardId);
  };

  const handleBuyNow = () => {
    console.log('Buy now clicked');
  };

  const formatCardNumber = (value: string) => {
    const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
    const matches = v.match(/\d{4,16}/g);
    const match = (matches && matches[0]) || '';
    const parts = [];

    for (let i = 0, len = match.length; i < len; i += 4) {
      parts.push(match.substring(i, i + 4));
    }

    if (parts.length) {
      return parts.join(' ');
    } else {
      return v;
    }
  };

  return (
    <div>
      <SettingMenu />
      <div className="bg-white rounded-md shadow-lg p-6 mx-auto space-y-8">
        {/* Current Plan Section */}
        <div className="relative">
          {/* Settings Icon */}
          <button className="absolute top-0 right-0 p-2 text-gray-400 hover:text-gray-600">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>
          </button>

          <h2 className="text-xl font-semibold text-gray-800 mb-6">Current Plan</h2>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Plan Details */}
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-medium text-gray-800">Your Current Plan is Basic</h3>
                <p className="text-gray-600 text-sm">A simple start for everyone</p>
              </div>

              <div>
                <p className="text-gray-800 font-medium">Active until Dec 09, 2021</p>
                <p className="text-gray-600 text-sm">
                  We will send you a notification upon Subscription expiration
                </p>
              </div>

              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <span className="text-gray-800 font-semibold text-lg">$199 Per Month</span>
                  <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded-full font-medium">
                    Popular
                  </span>
                </div>
                <p className="text-gray-600 text-sm">
                  Standard plan for small to medium businesses
                </p>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  type="button"
                  onClick={handleUpgradePlan}
                  className="px-6 btn-primary w-40 mr-0 ml-0 mt-0">
                  Upgrade Plan
                </button>
                <button
                  type="button"
                  onClick={handleCancelSubscription}
                  className="px-6 py-2 bg-red-50 text-red-600 rounded-md hover:bg-red-100 transition-colors text-sm font-medium border border-red-200">
                  Cancel Subscription
                </button>
              </div>
            </div>

            {/* Alert Box */}
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 mt-1">
                  <svg className="w-5 h-5 text-orange-500" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold text-orange-600 mb-1">We need your attention!</h4>
                  <p className="text-orange-600 text-sm mb-4">Your plan requires update</p>

                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-700 font-medium text-sm">Days</span>
                      <span className="text-gray-700 font-medium text-sm">12 of 30 Days</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-purple-600 h-2 rounded-full"
                        style={{ width: '40%' }}></div>
                    </div>
                    <p className="text-gray-600 text-sm">
                      18 days remaining until your plan requires update
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Payment Methods Section */}
        <div className="border-t pt-8">
          <h2 className="text-xl font-semibold text-gray-800 mb-6">Payment Methods</h2>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Payment Form */}
            <div className="space-y-6">
              {/* Payment Method Selection */}
              <div className="space-y-4">
                <div className="flex items-center space-x-6">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      name="paymentMethod"
                      value="card"
                      checked={paymentMethod === 'card'}
                      onChange={(e) => setPaymentMethod(e.target.value as 'card')}
                      className="w-4 h-4 text-purple-600 border-gray-300 focus:ring-purple-500"
                    />
                    <span className="ml-2 text-gray-700 font-medium">Credit/Debit/ATM Card</span>
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      name="paymentMethod"
                      value="paypal"
                      checked={paymentMethod === 'paypal'}
                      onChange={(e) => setPaymentMethod(e.target.value as 'paypal')}
                      className="w-4 h-4 text-purple-600 border-gray-300 focus:ring-purple-500"
                    />
                    <span className="ml-2 text-gray-700 font-medium">Paypal account</span>
                  </label>
                </div>
              </div>

              {/* Card Form */}
              {paymentMethod === 'card' && (
                <div className="space-y-4">
                  {/* Card Number */}
                  <div className="form-group">
                    <label className="block mb-1 font-medium text-gray-700">Card Number</label>
                    <div className="relative">
                      <input
                        type="text"
                        className="input-style pr-12"
                        placeholder="1356 3215 6548 7898"
                        maxLength={19}
                        {...register('cardNumber', {
                          required: true,
                          onChange: (e) => {
                            e.target.value = formatCardNumber(e.target.value);
                          }
                        })}
                      />
                      <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                        <div className="w-6 h-6 bg-red-100 rounded flex items-center justify-center">
                          <div className="w-3 h-3 bg-red-500 rounded-sm flex items-center justify-center">
                            <span className="text-white text-xs">!</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    {errors.cardNumber && (
                      <p className="text-red-500 text-sm mt-1">Card number is required</p>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    {/* Name */}
                    <div className="form-group">
                      <label className="block mb-1 font-medium text-gray-700">Name</label>
                      <input
                        type="text"
                        className="input-style"
                        placeholder="John Doe"
                        {...register('name', {
                          required: true
                        })}
                      />
                      {errors.name && <p className="text-red-500 text-sm mt-1">Name is required</p>}
                    </div>

                    {/* Exp. Date */}
                    <div className="form-group">
                      <label className="block mb-1 font-medium text-gray-700">Exp. Date</label>
                      <input
                        type="text"
                        className="input-style"
                        placeholder="MM/YY"
                        maxLength={5}
                        {...register('expiryDate', {
                          required: true,
                          pattern: /^(0[1-9]|1[0-2])\/([0-9]{2})$/
                        })}
                      />
                      {errors.expiryDate && (
                        <p className="text-red-500 text-sm mt-1">Valid expiry date required</p>
                      )}
                    </div>
                  </div>

                  {/* CVV Code */}
                  <div className="form-group">
                    <label className="block mb-1 font-medium text-gray-700">CVV Code</label>
                    <div className="relative max-w-32">
                      <input
                        type={showCVV ? 'text' : 'password'}
                        className="input-style pr-8"
                        placeholder="654"
                        maxLength={4}
                        {...register('cvv', {
                          required: true,
                          pattern: /^[0-9]{3,4}$/
                        })}
                      />
                      <button
                        type="button"
                        className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600">
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                      </button>
                    </div>
                    {errors.cvv && <p className="text-red-500 text-sm mt-1">Valid CVV required</p>}
                  </div>
                </div>
              )}
            </div>

            {/* My Cards Section */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-800">My Cards</h3>

              <div className="space-y-4">
                {savedCards.map((card) => (
                  <div key={card.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {/* Card Icon */}
                        <div className="w-8 h-6 rounded bg-gradient-to-r from-red-500 to-yellow-500 flex items-center justify-center">
                          {card.type === 'mastercard' ? (
                            <div className="flex">
                              <div className="w-3 h-3 bg-red-500 rounded-full opacity-80"></div>
                              <div className="w-3 h-3 bg-yellow-400 rounded-full -ml-1"></div>
                            </div>
                          ) : (
                            <div className="text-white text-xs font-bold">VISA</div>
                          )}
                        </div>

                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-gray-800">{card.name}</span>
                            {card.isPrimary && (
                              <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded font-medium">
                                Primary
                              </span>
                            )}
                          </div>
                          <div className="text-gray-600 text-sm">•••• •••• {card.lastFour}</div>
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => handleEditCard(card.id)}
                          className="px-3 py-1 text-purple-600 hover:text-purple-700 text-sm font-medium">
                          Edit
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDeleteCard(card.id)}
                          className="px-3 py-1 text-red-600 hover:text-red-700 text-sm font-medium">
                          Delete
                        </button>
                      </div>
                    </div>

                    <div className="mt-2 text-gray-500 text-xs">
                      Card expires at {card.expiryDate}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Buy Now Button */}
          <div className="mt-8 flex justify-end">
            <button
              type="button"
              onClick={handleBuyNow}
              className="px-8 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 transition-colors font-medium">
              Buy Now
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BillingPlansComponent;
